import React, { useState, useRef, useEffect } from 'react';
import { CSSTransition } from 'react-transition-group';
import { MapInteraction } from 'react-map-interaction';

export default function ViewLevelViewEnlarged({ view, onClose }) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgSize, setImgSize] = useState({ w: 1920, h: 1080 });
  const containerRef = useRef(null);

  // Keep last valid view so src/caption don't blank during fade-out
  const lastViewRef = useRef(null);
  if (view) lastViewRef.current = view;
  const displayView = view || lastViewRef.current;

  const [mapValue, setMapValue] = useState({ scale: 1, translation: { x: 0, y: 0 } });

  // On image load, compute fit-to-container scale and center translation
  const handleImgLoad = (e) => {
    const naturalW = e.target.naturalWidth;
    const naturalH = e.target.naturalHeight;
    setImgSize({ w: naturalW, h: naturalH });

    const containerW = containerRef.current?.offsetWidth || window.innerWidth;
    const containerH = containerRef.current?.offsetHeight || window.innerHeight;

    const scaleX = containerW / naturalW;
    const scaleY = containerH / naturalH;
    const scale = Math.min(scaleX, scaleY);

    const tx = (containerW - naturalW * scale) / 2;
    const ty = (containerH - naturalH * scale) / 2;

    setMapValue({ scale, translation: { x: tx, y: ty } });
    setImgLoaded(true);
  };

  useEffect(() => {
    if (view) {
      setImgLoaded(false);
    }
  }, [view]);

  // minScale: fit-to-screen (recomputed after load; start at a sensible default)
  const containerW = containerRef.current?.offsetWidth || window.innerWidth;
  const containerH = containerRef.current?.offsetHeight || window.innerHeight;
  const fitScale = Math.min(containerW / imgSize.w, containerH / imgSize.h);

  return (
    <CSSTransition
      in={!!view}
      timeout={300}
      classNames='level-view-fade'
      unmountOnExit
    >
      <div className='level-view-enlarged' ref={containerRef}>
        <div
          className='level-view-enlarged--inner'
          style={{ opacity: imgLoaded ? 1 : 0, transition: 'opacity 300ms ease' }}
        >
          <MapInteraction
            value={mapValue}
            onChange={setMapValue}
            minScale={fitScale}
            maxScale={4}
          >
            {({ translation, scale }) => (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden',
                }}
              >
                {/* Transformed layer */}
                <div
                  style={{
                    position: 'absolute',
                    transform: `translate(${translation.x}px, ${translation.y}px) scale(${scale})`,
                    transformOrigin: '0 0',
                  }}
                >
                  {/* Image */}
                  <img
                    src={displayView ? `./img/views/${displayView.img}` : ''}
                    alt={displayView ? displayView.caption : ''}
                    draggable={false}
                    onLoad={handleImgLoad}
                    style={{
                      display: 'block',
                      userSelect: 'none',
                      pointerEvents: 'none',
                    }}
                  />
                  {/* Caption — anchored to bottom-left of the actual image */}
                  {displayView?.caption && (
                    <div className='level-view-enlarged--caption'>
                      {displayView.caption}
                    </div>
                  )}
                </div>
              </div>
            )}
          </MapInteraction>
        </div>

        <button className='level-view-enlarged--close-btn' onClick={onClose} />
      </div>
    </CSSTransition>
  );
}
